import { ACTIVITY_STATS, flattenPersonalStats, personalStatsOf, type ActivityDays, type PlayerStatsAnswer } from '@omni-torn/shared';
import { server, torn } from './session';

/**
 * Another player's PUBLIC personal stats, two ways — shown on the page, never kept or sent anywhere by the website:
 *  - with the viewer's own key, from this browser (or through the extension, siteRead): Personal stats → Compare
 *    (pages/stats.ts, S01) and the player page (pages/lookup.ts, S06);
 *  - from the Omni-Torn server (GET /api/players/:id/stats: lent keys first, else the server's own key; kept there an hour
 *    in memory, capped a minute) — the player page only, for viewers who'd rather not spend their own calls.
 */

export interface TheirStats {
  id: number;
  name: string;
  level: number | null;
  /** Every personal stat Torn shows of them, flat (v1 names). */
  ps: Record<string, number>;
}

/** Their public profile and every personal stat: one Torn call with your key (the same answer is reused for 5 minutes). */
export async function theirStatsWithMyKey(id: number): Promise<TheirStats> {
  const r = (await torn(`/user/${id}`, { selections: 'profile,personalstats' }, 300_000)) as { name?: unknown; level?: unknown; personalstats?: unknown };
  const ps = flattenPersonalStats(r.personalstats);
  if (!Object.keys(ps).length) throw new Error('Torn didn’t send their personal stats.');
  return { id, name: typeof r.name === 'string' && r.name ? r.name : `#${id}`, level: typeof r.level === 'number' ? r.level : null, ps };
}

/** A player's ten activity stats now and N days ago, and where they came from. */
export interface ActivityRead {
  id: number;
  /** From Torn's profile (your key) — the server's read has no name; the page fills it in. */
  name: string | null;
  level: number | null;
  days: ActivityDays;
  now: Record<string, number>;
  /** Null: Torn had nothing that far back (a newer account) — the totals stand in. */
  then: Record<string, number> | null;
  /** When Torn answered (ms). */
  at: number;
  source: 'key' | 'server';
  problem?: string;
}

/** With your key: the profile + personal stats (reused 5 min) and the ten stats N days ago (to the hour; reused an hour). */
export async function activityWithMyKey(id: number, days: ActivityDays): Promise<ActivityRead> {
  const now = await theirStatsWithMyKey(id);
  const ts = Math.floor((Date.now() / 1000 - days * 86_400) / 3600) * 3600;
  const past = await torn(`/user/${id}`, { selections: 'personalstats', stat: ACTIVITY_STATS.join(','), timestamp: String(ts) }, 3_600_000);
  const then = personalStatsOf(past);
  return { id, name: now.name, level: now.level, days, now: now.ps, then: Object.keys(then).length ? then : null, at: Date.now(), source: 'key' };
}

/** From the Omni-Torn server (no calls of yours). `keptOnly`: only what it already has (no Torn call at all); null when nothing. */
export async function activityFromServer(id: number, days: ActivityDays, keptOnly = false): Promise<ActivityRead | null> {
  const a = await server<PlayerStatsAnswer>('GET', `/api/players/${id}/stats?days=${days}${keptOnly ? '&kept=1' : ''}`);
  if (!a.now) {
    if (keptOnly) return null;
    throw new Error(a.problem ?? 'The server had nothing for them.');
  }
  return { id, name: null, level: null, days, now: a.now.stats, then: a.then?.stats ?? null, at: a.now.at, source: 'server', ...(a.problem ? { problem: a.problem } : {}) };
}
